import { forwardRef, Fragment, useCallback, useEffect, useImperativeHandle, useMemo, useReducer, useRef, useState } from 'react';
import { assertNever } from '@sas/nova-core/typeguards';
import { Date as SvgDate, DateTime as SvgDateTime, Time as SvgTime } from '@sas/react-svg';
import cx from 'classnames';
import { createUseHtmlId } from '../../hooks/useHtmlId';
import { useMounted } from '../../hooks/useMounted';
import { createUseTextLocalizer } from '../../locale/createUseTextLocalizer';
import { useFormatLocale } from '../../locale/formatting/useFormatLocale';
import { renderDesignTokens } from '../../theme';
import { isAfterDate, isPastDate } from '../Calendar/DisabledDayFilters';
import { CompositeInput } from '../CompositeInput';
import { useInputValidationAdapter, validateTextInput } from '../Input/useInputValidationAdapter';
import { InvisibleText } from '../InvisibleText';
import { withLabels } from '../withLabels';
import { DatePickerPopover } from './DatePickerPopover';
import { getDateByWeekNumber, getDayPeriodOrder, getDayPeriods, getMonthAndYearByDate, getTimeSeparator, getYearOrder, isSameDay, MAX_TIME, MILLISECONDS_IN_HOUR, MIN_TIME } from './DateUtil';
import { getFractionalSecondSeparator } from './Pickers/TimePicker';
import { useDefaultDateTimeFormat } from './useDefaultDateTimeFormat';
import bundle from './NovaComponents-DatePicker-gui-icu.properties';
import './DatePicker.css';
import cn from './DatePicker.css.json';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const useTextLocalizer = createUseTextLocalizer(bundle);
const useHtmlId = createUseHtmlId('DatePicker');
const useDatePickerButtonId = createUseHtmlId('DatePicker-button'); // icon button
const useValidationMessageId = createUseHtmlId('DatePicker-ValidationMessage');
function hasTimePickerProps(x) {
  return x.type === 'time' || x.type === 'datetime';
}
export const DatePicker = /*#__PURE__*/forwardRef(function DatePicker(props, ref) {
  const defaultFormat = useDefaultDateTimeFormat({
    type: props.type ?? 'date',
    showMilliseconds: hasTimePickerProps(props) && props.showMilliseconds,
    showHours: hasTimePickerProps(props) && props.showHours,
    showMinutes: hasTimePickerProps(props) && props.showMinutes,
    showSeconds: hasTimePickerProps(props) && props.showSeconds,
    showDayPeriod: hasTimePickerProps(props) && props.showDayPeriod,
    showHoursDuration: hasTimePickerProps(props) && props.showHoursDuration,
    showMinutesDuration: hasTimePickerProps(props) && props.showMinutesDuration,
    show24HourClock: hasTimePickerProps(props) && props.show24HourClock,
    roundSeconds: hasTimePickerProps(props) && props.roundSeconds,
    firstDayOfWeek: props.type === 'week' ? props.firstDayOfWeek : undefined
  });
  const textValidator = useInputValidationAdapter({
    isRequired: true,
    initialValue: props.value ? (props.format ?? defaultFormat)(props.value) : ''
  });
  const refPicker = useRef(null);
  const type = props.type ?? 'date';
  const closePickerOnTodayPress = props.closePickerOnTodayButtonPress ?? type === 'date' ? true : false;
  const localize = useTextLocalizer();
  const locale = useFormatLocale();
  const mountedRef = useMounted();
  const defaultMinDate = useMemo(() => new Date(1582, 0, 1), []);
  const defaultMaxDate = useMemo(() => new Date(9999, 11, 31), []);
  const minDate = useMemo(() => {
    if (props.minDate && isAfterDate(props.minDate, {
      today: defaultMinDate
    }) && (isPastDate(props.minDate, {
      today: defaultMaxDate
    }) || isSameDay(props.minDate, defaultMaxDate))) {
      const mnDate = new Date(props.minDate);
      if (props.type === 'datetime' && !props.showMilliseconds) mnDate.setMilliseconds(0);
      if (props.type === 'month') {
        // Set to 1st day of month
        mnDate.setDate(1);
        mnDate.setHours(0);
        mnDate.setMinutes(0);
        mnDate.setSeconds(0);
      } else if (props.type === 'quarter') {
        // Set minDate to beginning of the quarter
        mnDate.setDate(1);
        if (mnDate.getMonth() >= 0 && mnDate.getMonth() < 3) {
          mnDate.setMonth(0);
        } else if (mnDate.getMonth() >= 3 && mnDate.getMonth() < 6) {
          mnDate.setMonth(3);
        } else if (mnDate.getMonth() >= 6 && mnDate.getMonth() < 9) {
          mnDate.setMonth(6);
        } else if (mnDate.getMonth() >= 9) {
          mnDate.setMonth(9);
        }
        mnDate.setHours(0);
        mnDate.setMinutes(0);
        mnDate.setSeconds(0);
      } else if (props.type === 'week') {
        const day = mnDate.getDay();
        if (props.firstDayOfWeek === 1) {
          // Set minDate to beginning of the week with Monday as the first day of the week
          // Calculate the difference between the given date and the first day of the week (Monday)
          const diff = day === 0 ? 6 : day - 1; // Adjust if the day is Sunday (0)
          mnDate.setDate(mnDate.getDate() - diff);
        } else {
          // Set minDate to beginning of the week with Sunday as the first day of the week
          // Calculate the difference between the given date and the first day of the week (Sunday)
          mnDate.setDate(mnDate.getDate() - day);
        }
        mnDate.setHours(0);
        mnDate.setMinutes(0);
        mnDate.setSeconds(0);
      }
      return mnDate;
    } else return new Date(defaultMinDate);
  }, [defaultMaxDate, defaultMinDate, props.minDate, props.showMilliseconds, props.type, props.firstDayOfWeek]);
  const maxDate = useMemo(() => {
    if (props.maxDate && (isAfterDate(props.maxDate, {
      today: defaultMinDate
    }) || isSameDay(props.maxDate, defaultMinDate)) && isPastDate(props.maxDate, {
      today: defaultMaxDate
    })) {
      const mxDate = new Date(props.maxDate);
      if (props.type === 'datetime' && !props.showMilliseconds) mxDate.setMilliseconds(0);
      if (props.type === 'month') {
        // Set to last day of month
        mxDate.setDate(new Date(mxDate.getFullYear(), mxDate.getMonth() + 1, 0).getDate());
        mxDate.setHours(23);
        mxDate.setMinutes(59);
        mxDate.setSeconds(59);
      } else if (props.type === 'quarter') {
        // Set maxDate to end of the quarter
        mxDate.setDate(1);
        if (mxDate.getMonth() >= 0 && mxDate.getMonth() < 3) {
          mxDate.setMonth(2);
        } else if (mxDate.getMonth() >= 3 && mxDate.getMonth() < 6) {
          mxDate.setMonth(5);
        } else if (mxDate.getMonth() >= 6 && mxDate.getMonth() < 9) {
          mxDate.setMonth(8);
        } else if (mxDate.getMonth() >= 9) {
          mxDate.setMonth(11);
        }
        mxDate.setDate(new Date(mxDate.getFullYear(), mxDate.getMonth() + 1, 0).getDate());
        mxDate.setHours(23);
        mxDate.setMinutes(59);
        mxDate.setSeconds(59);
      } else if (props.type === 'week') {
        const day = mxDate.getDay();
        if (props.firstDayOfWeek === 1) {
          // Set maxDate to end of the week with Monday as the first day of the week
          // Calculate the difference between the given date and the last day of the week (Sunday)
          const diff = day === 0 ? 0 : 7 - day; // Adjust if the day is Sunday (0)
          mxDate.setDate(mxDate.getDate() + diff);
        } else {
          // Set maxDate to end of the week with Sunday as the first day of the week
          // Calculate the difference between the given date and the last day of the week (Saturday)
          const diff = day === 0 ? 6 : 6 - day; // Adjust if the day is Sunday (0)
          mxDate.setDate(mxDate.getDate() + diff);
        }
        mxDate.setHours(23);
        mxDate.setMinutes(59);
        mxDate.setSeconds(59);
      }
      return mxDate;
    } else return new Date(defaultMaxDate);
  }, [defaultMaxDate, defaultMinDate, props.maxDate, props.showMilliseconds, props.type, props.firstDayOfWeek]);
  const isBeforeMinDate = useCallback(day => isPastDate(day, {
    today: minDate
  }), [minDate]);
  const isAfterMaxDate = useCallback(day => isAfterDate(day, {
    today: maxDate
  }), [maxDate]);

  /**
   * Possible values:
   * - **unfocused**: the user isn't editing the value
   * - **editing**: the user is editing the value
   * - **committed**: the user just committed a value but is still editing.
   */

  const [editState, setEditState] = useState('unfocused');

  // For use when editable and/or required
  const [value, setValue] = useState(props.value ? (props.format ?? defaultFormat)(props.value) : '');
  const [state, setState] = useState(props.value ?? new Date());
  {
    const [prevValue, setPrevValue] = useState(props.value);
    if ((!!prevValue ? prevValue.getTime() : 0) !== (!!props.value ? props.value.getTime() : 0)) {
      setPrevValue(props.value);
      setState(props.value ?? new Date());
      let newValue = '';
      if (props.editable) {
        if (props.value === null) {
          setValue('');
          newValue = '';
        } else if (editState === 'editing') {
          setValue(value);
          newValue = value;
        } else {
          newValue = (props.format ?? defaultFormat)(props.value);
          setValue(newValue);
        }
      } else {
        if (props.value === null) {
          setValue('');
          newValue = '';
        } else {
          newValue = (props.format ?? defaultFormat)(props.value);
          setValue(newValue);
        }
      }
      const changeEvent = {
        value: newValue
      };
      textValidator.handleChange(changeEvent);
    }
  }
  let buttonIcon;
  let roleDescription = localize('date.role.aria');
  let iconTooltip = localize('date.button.tooltip.label');
  switch (props.type) {
    case null:
    case undefined:
    case 'date':
      buttonIcon = SvgDate;
      iconTooltip = localize('date.button.tooltip.label');
      roleDescription = localize('date.role.aria');
      break;
    case 'datetime':
      buttonIcon = SvgDateTime;
      iconTooltip = localize('datetime.button.tooltip.label');
      roleDescription = localize('datetime.role.aria');
      break;
    case 'month':
      buttonIcon = SvgDate;
      iconTooltip = localize('month.button.tooltip.label');
      roleDescription = localize('month.role.aria');
      break;
    case 'quarter':
      buttonIcon = SvgDate;
      iconTooltip = localize('quarter.button.tooltip.label');
      roleDescription = localize('quarter.role.aria');
      break;
    case 'week':
      buttonIcon = SvgDate;
      iconTooltip = localize('week.button.tooltip.label');
      roleDescription = localize('week.role.aria');
      break;
    case 'time':
      buttonIcon = SvgTime;
      iconTooltip = localize('time.button.tooltip.label');
      roleDescription = localize('time.role.aria');
      break;
    default:
      assertNever(props);
  }
  const buttonRef = useRef(null);
  const clearButtonRef = useRef(null);
  const [hidePicker, setHidePicker] = useState(true);
  const [isValid, setIsValid] = useState(true);
  const [isMinMaxValid, setIsMinMaxValid] = useState(true);
  const prevStatus = useRef(props.status);
  const getParsedValue = useCallback(value => {
    const dateValue = new Date();
    if (props.type === 'quarter' || props.type === 'week') {
      const yearOrder = getYearOrder(locale);
      const parts = value.split(' ');
      const year = yearOrder === 'after' ? parts[1] : parts[0];
      const dtVal = yearOrder === 'after' ? parts[0].substring(1) : parts[1].substring(1);
      dateValue.setDate(1);
      if (props.type === 'quarter') {
        if (dtVal === '1') {
          dateValue.setMonth(0);
        } else if (dtVal === '2') {
          dateValue.setMonth(3);
        } else if (dtVal === '3') {
          dateValue.setMonth(6);
        } else if (dtVal === '4') {
          dateValue.setMonth(9);
        }
        dateValue.setFullYear(parseInt(year));
        return dateValue.toString();
      } else if (props.type === 'week') {
        return getDateByWeekNumber(parseInt(dtVal), parseInt(year)).toString();
      }
    } else if (props.type === 'time') {
      const dayPeriodOrder = getDayPeriodOrder(locale);
      const h24clock = dayPeriodOrder === 'none';
      const dayPeriods = getDayPeriods(locale);
      const timeSeparator = getTimeSeparator(locale);
      const millisecondsSeparator = getFractionalSecondSeparator(locale);
      const timeParts = value.split(timeSeparator);
      const ampm = h24clock ? null : dayPeriodOrder === 'before' ? timeParts[0].split(' ')[0] : timeParts[2].indexOf(millisecondsSeparator) !== -1 ? timeParts[2].replace('\u202F', ' ').split(' ')[1] : timeParts[2].split(' ')[1];
      let hours = parseInt(dayPeriodOrder === 'before' ? timeParts[0].split(' ')[1] : timeParts[0]);
      const seconds = parseInt(dayPeriodOrder === 'after' ? timeParts[2].split(' ')[0] : timeParts[2]);
      const milliSeconds = props.showMilliseconds ? parseInt(dayPeriodOrder === 'after' ? timeParts[2].split('.')[1].split(' ')[0] : timeParts[2].split('.')[1]) : 0;
      if (ampm && ampm === dayPeriods[0] && hours === 12) {
        hours = 0;
      } else if (ampm && ampm === dayPeriods[1] && hours !== 12) {
        hours += 12;
      }
      dateValue.setHours(hours);
      dateValue.setMinutes(parseInt(timeParts[1]));
      dateValue.setSeconds(seconds);
      dateValue.setMilliseconds(milliSeconds);
      return dateValue.toJSON();
    }
    return '';
  }, [locale, props.showMilliseconds, props.type]);
  const handleChange = e => {
    if (!mountedRef.current) return;
    setValue(e.value);
    if (props.editable) {
      textValidator.handleChange(e);
      setEditState(prevState => prevState !== 'unfocused' ? 'editing' : prevState);
      if (e.value === '') props.onChange({
        value: null
      });
    } else if (e.value === '') {
      inputRef.current?.focus();
      setState(new Date());
      setIsMinMaxValid(true);
      props.onChange({
        value: null
      });
    }
  };
  const handleCommit = useCallback(e => {
    if (!mountedRef.current) return;
    const valueToCommit = props.editable && (props.type === 'quarter' || props.type === 'week' || props.type === 'time') ? getParsedValue(e.value) : e.value;
    let d = new Date(valueToCommit);
    if (!isNaN(d.getTime())) {
      if (isBeforeMinDate(d) || isAfterMaxDate(d)) {
        setIsMinMaxValid(false);
        setIsValid(false);
        d = new Date();
      } else {
        setIsMinMaxValid(true);
        setIsValid(true);
      }
      setState(d);
      props.onChange({
        value: d
      });
    } else {
      if (props.required && e.value === '') {
        setIsValid(false);
      } else {
        setIsMinMaxValid(true);
        setIsValid(true);
      }
    }
    setEditState(prevState => prevState === 'editing' ? 'committed' : prevState);
  }, [getParsedValue, isAfterMaxDate, isBeforeMinDate, mountedRef, props]);
  const handleBlur = () => {
    if (!mountedRef.current) return;
    if (props.required) {
      const result = validateTextInput({
        value,
        isRequired: true
      });
      setIsValid(!result.isError);
    } else if (props.status) {
      setIsValid(false);
    } else setIsValid(true);
  };
  const inputRef = useRef(null);
  useImperativeHandle(ref, () => ({
    focus() {
      inputRef.current?.focus({
        preventScroll: props.preventScroll
      });
    }
  }));
  const [focusState, dispatchFocusAction] = useReducer((prevState, action) => {
    switch (action.type) {
      case 'dayStep':
        {
          const nextDate = new Date(prevState);
          if (action.direction === 'next') {
            nextDate.setDate(nextDate.getDate() + 1);
          } else if (action.direction === 'previous') {
            nextDate.setDate(nextDate.getDate() - 1);
          }
          if (isBeforeMinDate(nextDate) || isAfterMaxDate(nextDate)) return new Date(prevState);
          props.onChange({
            value: nextDate
          });
          return nextDate;
        }
      case 'monthStep':
        {
          const nextDate = new Date(prevState);
          if (action.direction === 'next') {
            nextDate.setMonth(nextDate.getMonth() + 1);
          } else if (action.direction === 'previous') {
            nextDate.setMonth(nextDate.getMonth() - 1);
          }
          if (isBeforeMinDate(nextDate) || isAfterMaxDate(nextDate)) return new Date(prevState);
          props.onChange({
            value: nextDate
          });
          return nextDate;
        }
      case 'yearStep':
        {
          const nextDate = new Date(prevState);
          if (action.direction === 'next') {
            nextDate.setFullYear(nextDate.getFullYear() + 1);
          } else if (action.direction === 'previous') {
            nextDate.setFullYear(nextDate.getFullYear() - 1);
          }
          if (isBeforeMinDate(nextDate) || isAfterMaxDate(nextDate)) return new Date(prevState);
          props.onChange({
            value: nextDate
          });
          return nextDate;
        }
      case 'hourStep':
        {
          const nextDate = new Date(prevState);
          if (action.direction === 'next') {
            nextDate.getTime() + MILLISECONDS_IN_HOUR * 1 < MAX_TIME && nextDate.setHours(nextDate.getHours() + 1);
          } else if (action.direction === 'previous') {
            nextDate.getTime() - MILLISECONDS_IN_HOUR * 1 > MIN_TIME && nextDate.setHours(nextDate.getHours() - 1);
          }
          props.onChange({
            value: nextDate
          });
          return nextDate;
        }
      case 'minuteStep':
        {
          const nextDate = new Date(prevState);
          if (action.direction === 'next') {
            nextDate.setMinutes(nextDate.getMinutes() + 1);
          } else if (action.direction === 'previous') {
            nextDate.setMinutes(nextDate.getMinutes() - 1);
          }
          props.onChange({
            value: nextDate
          });
          return nextDate;
        }
      case 'secondStep':
        {
          const nextDate = new Date(prevState);
          if (action.direction === 'next') {
            nextDate.setSeconds(nextDate.getSeconds() + 1);
          } else if (action.direction === 'previous') {
            nextDate.setSeconds(nextDate.getSeconds() - 1);
          }
          props.onChange({
            value: nextDate
          });
          return nextDate;
        }
      case 'selection':
        return action.value;
      default:
        return prevState;
    }
  }, state);
  useEffect(() => {
    if (props.value && isPastDate(new Date(props.value), {
      today: minDate
    }) || props.value && isAfterDate(new Date(props.value), {
      today: maxDate
    })) {
      setIsMinMaxValid(false);
    } else {
      setIsMinMaxValid(true);
    }
    const handleMouseUp = event => {
      if (event.button === 2) {
        !props.editable ? setHidePicker(true) : undefined;
      } else if (event.button === 0) {
        if (!props.readOnly && !props.editable) {
          setHidePicker(prev => !prev);
        }
      }
    };
    const handleFocusOut = () => {
      if (!mountedRef.current) return;
      props.required && textValidator.handleBlur();
      // Input has a bug where it doesn't fire the onCommit event when focus moves away from the input
      // if the reset button is present. This is a workaround to ensure the value is committed.
      props.editable && props.enableResetButton && handleCommit({
        value,
        reason: 'enter'
      });
      setTimeout(() => setEditState('unfocused'), 0);
    };
    const handleInputKeyDown = e => {
      if (props.readOnly || props.disabled) return;
      if (props.editable) setEditState(prevState => prevState !== 'committed' ? 'editing' : prevState);
      const key = e.key;
      const alt = e.altKey;
      const ctrl = e.ctrlKey;
      const shift = e.shiftKey;
      switch (key) {
        case ' ':
          if (!props.editable) setHidePicker(prev => !prev);
          break;
        case 'Spacebar':
          if (!props.editable) setHidePicker(prev => !prev);
          break;
        case 'Enter':
          e.preventDefault();
          if (!props.editable) setHidePicker(prev => !prev);else {
            const commitEvent = {
              value
            };
            handleCommit(commitEvent);
          }
          break;
        case 'ArrowUp':
          if (alt) setHidePicker(prev => !prev);
          break;
        case 'ArrowDown':
          if (alt) setHidePicker(prev => !prev);
          break;
        case 'PageUp':
          if (e.target.value === '') return;
          if (ctrl && shift && props.type === 'time') dispatchFocusAction({
            type: 'secondStep',
            direction: 'previous'
          });else if (ctrl && shift) dispatchFocusAction({
            type: 'yearStep',
            direction: 'previous'
          });else if (shift && props.type === 'time') dispatchFocusAction({
            type: 'minuteStep',
            direction: 'previous'
          });else if (shift) dispatchFocusAction({
            type: 'monthStep',
            direction: 'previous'
          });else if (props.type === 'time') dispatchFocusAction({
            type: 'hourStep',
            direction: 'previous'
          });else dispatchFocusAction({
            type: 'dayStep',
            direction: 'previous'
          });
          e.preventDefault();
          break;
        case 'PageDown':
          if (e.target.value === '') return;
          if (ctrl && shift && props.type === 'time') dispatchFocusAction({
            type: 'secondStep',
            direction: 'next'
          });else if (ctrl && shift) dispatchFocusAction({
            type: 'yearStep',
            direction: 'next'
          });else if (shift && props.type === 'time') dispatchFocusAction({
            type: 'minuteStep',
            direction: 'next'
          });else if (shift) dispatchFocusAction({
            type: 'monthStep',
            direction: 'next'
          });else if (props.type === 'time') dispatchFocusAction({
            type: 'hourStep',
            direction: 'next'
          });else dispatchFocusAction({
            type: 'dayStep',
            direction: 'next'
          });
          e.preventDefault();
          break;
        case 'Escape':
          props.onCancel?.();
          break;
        case 'Delete':
        case 'Backspace':
          if (!props.enableResetButton && !props.editable) e.preventDefault();
          break;
        default:
          if (!props.editable || editState !== 'editing') {
            if (e.target.value === '') return;
            if (state !== props.value) props.onChange({
              value: new Date(state)
            });
          }
          break;
      }
    };
    const input = inputRef.current;
    input?.addEventListener('keydown', handleInputKeyDown);
    input?.addEventListener('mouseup', handleMouseUp);
    input?.addEventListener('focusout', handleFocusOut);
    if (!props.editable) inputRef.current?.setAttribute('aria-readonly', 'true');
    return function () {
      input?.removeEventListener('keydown', handleInputKeyDown);
      input?.removeEventListener('mouseup', handleMouseUp);
      input?.removeEventListener('focusout', handleFocusOut);
    };
  }, [props, focusState, state, value, defaultFormat, minDate, maxDate, textValidator, handleCommit, editState, mountedRef]);
  const idValidationMessage = useValidationMessageId();
  const invisibleTextId = useHtmlId();
  const datePickerButtonLabelId = useDatePickerButtonId();
  const ariaDescribedBy = [];
  const datePickerButtonAriaLabelledBy = [datePickerButtonLabelId];
  if (!props.disabled) {
    ariaDescribedBy.push(invisibleTextId);
    if (!props.editable) ariaDescribedBy.push(idValidationMessage);
  }
  if (props.ariaDescribedBy) {
    if (typeof props.ariaDescribedBy === 'string') {
      ariaDescribedBy.push(props.ariaDescribedBy);
    } else {
      ariaDescribedBy.push(...props.ariaDescribedBy);
    }
  }
  if (!!props.ariaLabelledBy) {
    datePickerButtonAriaLabelledBy.splice(0, 0, props.ariaLabelledBy);
  }
  const printMinMaxErrorMessage = useMemo(() => {
    const minMaxMonthErrorMsg = localize('minmax.month.error.msg', {
      min: getMonthAndYearByDate(locale, minDate),
      max: getMonthAndYearByDate(locale, maxDate)
    });
    const minMaxQuarterErrorMsg = localize('minmax.quarter.error.msg', {
      min: (props.format ?? defaultFormat)(minDate),
      max: (props.format ?? defaultFormat)(maxDate)
    });
    const minMaxDateErrorMsg = localize('minmax.date.error.msg', {
      min: (props.format ?? defaultFormat)(minDate),
      max: (props.format ?? defaultFormat)(maxDate)
    });
    const minMaxDateTimeErrorMsg = localize('minmax.datetime.error.msg', {
      min: (props.format ?? defaultFormat)(minDate),
      max: (props.format ?? defaultFormat)(maxDate)
    });
    switch (props.type) {
      case 'month':
        return minMaxMonthErrorMsg;
      case 'quarter':
        return minMaxQuarterErrorMsg;
      case 'datetime':
        return minMaxDateTimeErrorMsg;
      default:
        return minMaxDateErrorMsg;
    }
  }, [defaultFormat, locale, localize, maxDate, minDate, props.format, props.type]);
  const isDisabledDate = day => {
    if (props.showHoursDuration || props.showMinutesDuration) return false;
    if (isBeforeMinDate(day) || isAfterMaxDate(day)) return true;
    if (props.disabledDates) {
      return props.disabledDates.some(disabledDate => isSameDay(disabledDate, day));
    }
    if (props.enabledDates) {
      return props.enabledDates.every(enabledDate => !isSameDay(enabledDate, day));
    }
    return false;
  };
  useEffect(() => {
    if (props.status === 'alert') {
      setIsValid(false);
    } else if (props.status === null) {
      setIsValid(true);
    }
  }, [props.status]);
  const getMessage = useCallback(() => {
    return !isMinMaxValid ? props.message ?? printMinMaxErrorMessage : props.message ?? localize(props.status === 'warning' ? 'warning.msg' : 'required.error.msg');
  }, [isMinMaxValid, localize, printMinMaxErrorMessage, props.message, props.status]);
  const getStatus = useCallback(() => {
    if (!mountedRef.current) return;
    const status = props.status ? props.status : props.required ? !isValid || !isMinMaxValid ? 'alert' : textValidator.errors?.status : !props.showHoursDuration && !props.showMinutesDuration && (!isValid || !isMinMaxValid) ? 'alert' : undefined;
    // const status = !isValid || !isMinMaxValid || props.status === 'alert' ? 'alert' : undefined;
    if (status === prevStatus.current) return status;
    prevStatus.current = status;
    if (status === 'alert') {
      const message = getMessage();
      setTimeout(() => props.onStatusChange?.({
        status: 'alert',
        message
      }), 0);
    } else {
      setTimeout(() => props.onStatusChange?.({
        status: undefined,
        message: ''
      }), 0);
    }
    return status;
  }, [mountedRef, props, isValid, isMinMaxValid, textValidator.errors?.status, getMessage]);
  const showResetButton = typeof props.enableResetButton === 'boolean' ? props.enableResetButton : props.enableResetButton === undefined && !props.editable;
  return /*#__PURE__*/_jsxs(Fragment, {
    children: [/*#__PURE__*/_jsxs("div", {
      className: "date-picker",
      style: {
        width: '100%'
      },
      onFocus: event => {
        if (props.disabled) inputRef.current?.setAttribute('aria-roledescription', roleDescription);
        if (event.relatedTarget !== inputRef.current && event.relatedTarget !== buttonRef.current && (clearButtonRef.current === null || event.relatedTarget !== clearButtonRef.current)) {
          props.onFocusIn?.();
        }
      },
      onBlur: event => {
        // If the Calendar popup is open, we don't want to fire prop.onFocusOut
        if (!hidePicker) {
          return;
        }
        if (event.relatedTarget !== inputRef.current && event.relatedTarget !== buttonRef.current && (clearButtonRef.current === null || event.relatedTarget !== clearButtonRef.current)) {
          if (!props.editable) handleBlur();
          props.onFocusOut?.(refPicker.current?.isValid());
        }
      },
      children: [!props.disabled && props.editable ? /*#__PURE__*/_jsx(InvisibleText, {
        id: invisibleTextId,
        children: localize('edit.aria')
      }) : null, /*#__PURE__*/_jsx(CompositeInput, {
        testId: props.testId,
        style: props.style,
        className: props.className,
        mode: props.readOnly ? 'readonly' : props.editable ? 'combo' : 'select',
        buttonDisplay: buttonIcon,
        entryFieldRef: inputRef,
        buttonRef: buttonRef,
        commitOnEnterInDialog: props.editable !== true,
        disabled: props.disabled,
        onOpen: () => {
          setHidePicker(prev => !prev);
        },
        value: props.editable ? value : props.value ? (props.format ?? defaultFormat)(props.value) : '',
        entryFieldProps: {
          className: cx(!props.editable && cn.inputCursor),
          id: props.id,
          showClearButton: showResetButton,
          onChange: handleChange,
          onCommit: props.editable ? handleCommit : () => {},
          clearButtonRef,
          clearButtonTooltip: localize('date.resetButton.tooltip.label'),
          clearButtonAriaLabel: localize('date.resetButton.tooltip.label'),
          required: props.required,
          message: getMessage(),
          status: getStatus(),
          placeholder: props.inputPlaceholder
        },
        buttonProps: {
          tooltip: iconTooltip,
          ariaLabel: iconTooltip,
          className: cn.iconButton
        },
        ariaLabel: props.ariaLabel,
        ariaLabelledBy: props.ariaLabelledBy,
        ariaDescribedBy: ariaDescribedBy,
        "aria-roledescription": roleDescription,
        "aria-disabled": props.disabled
      })]
    }), /*#__PURE__*/_jsx(DatePickerPopover, {
      ...props,
      isOpen: !hidePicker && !props.disabled,
      value: state,
      onChange: ({
        value
      }) => {
        setTimeout(() => {
          if (!mountedRef.current) return;
          const newDate = value ?? new Date();
          if (type === 'datetime' || type === 'time' && !props.showHoursDuration && !props.showMinutesDuration) {
            if (props.showHours === false) newDate.setHours(0);
            if (props.showMinutes === false) newDate.setMinutes(0);
            if (props.showSeconds === false) newDate.setSeconds(0);
            if (!props.showMilliseconds) newDate.setMilliseconds(0);
          }
          setState(newDate);
        }, 0);
      },
      onEscape: () => {
        setHidePicker(true);
        setState(props.value ?? new Date());
      },
      onOkPress: () => {
        setHidePicker(true);
        if (!isDisabledDate(state)) {
          const value = new Date(state);
          if (type === 'datetime' || type === 'time' && !props.showHoursDuration && !props.showMinutesDuration) {
            if (props.showHours === false) value.setHours(0);
            if (props.showMinutes === false) value.setMinutes(0);
            if (props.showSeconds === false) value.setSeconds(0);
            if (!props.showMilliseconds) value.setMilliseconds(0);
          }
          // in case the user hits OK button without making a change in calendar, we need to make sure the time is set to 00:00:00.000
          if (type === 'date') {
            value.setHours(0);
            value.setMinutes(0);
            value.setSeconds(0);
            value.setMilliseconds(0);
          }
          if (value.getTime() !== state.getTime()) {
            setState(value);
          }
          dispatchFocusAction({
            type: 'selection',
            value
          });
          props.onChange({
            value
          });
          setValue((props.format ?? defaultFormat)(value));
          if (props.required) handleChange({
            value: (props.format ?? defaultFormat)(value)
          });
          setIsValid(true);
          setIsMinMaxValid(true);
        }
      },
      onCancelPress: () => {
        setHidePicker(true);
        setState(props.value ?? new Date());
      },
      onTodayButtonPress: date => {
        if (type !== 'date' && type !== 'datetime') return;
        closePickerOnTodayPress && setHidePicker(true);
        if (!isDisabledDate(date) && closePickerOnTodayPress) {
          const value = new Date(date);
          dispatchFocusAction({
            type: 'selection',
            value
          });
          props.onChange({
            value: date
          });
          setValue((props.format ?? defaultFormat)(date));
          if (props.required) handleChange({
            value: (props.format ?? defaultFormat)(date)
          });
          setIsValid(true);
          setIsMinMaxValid(true);
        }
        props.onTodayButtonPress?.(date);
        setTimeout(() => inputRef.current?.focus(), 0);
      },
      referenceElementRef: buttonRef
    })]
  });
});
export const DatePickerWithLabels = withLabels(DatePicker);

// TODO: Revisit to clean unused token under NOVACOMMONS-25177.
export const getDatePickerDesignTokens = (dt, opts) => {
  // Outline Focus Indicator
  const outline = 'none';
  let outlineOffset;
  if (dt.themeVariant !== 'high-contrast' && !opts?.isCustomFocusTurnedOn && !opts?.disabled && !opts?.readOnly) {
    outlineOffset = undefined;
  } else {
    outlineOffset = 'calc(-1 * var(--focus-width))';
  }
  const tokens = opts?.readOnly ? {
    bd: 'transparent',
    bg: dt.tokens['color-action-background-control-normal'],
    fg: dt.tokens['color-action-text-control-normal'],
    'hover-bd': 'var(--date-picker-bd)',
    'hover-bg': 'var(--date-picker-bg)',
    'hover-fg': 'var(--date-picker-fg)',
    'active-bd': 'var(--date-picker-bd)',
    'active-bg': 'var(--date-picker-bg)',
    'active-fg': 'var(--date-picker-fg)'
  } : {
    bd: dt.tokens['color-action-border-bottom-control-normal'],
    bg: dt.tokens['color-action-background-control-normal'],
    fg: dt.tokens['color-action-text-control-normal'],
    'hover-bd': dt.tokens['color-action-border-bottom-control-hover'],
    'hover-bg': dt.tokens['color-action-background-control-hover'],
    'hover-fg': dt.tokens['color-action-text-control-hover'],
    'active-bd': dt.tokens['color-action-border-bottom-control-selected'],
    'active-bg': dt.tokens['color-action-background-control-selected'],
    'active-fg': dt.tokens['color-action-text-control-selected']
  };
  return {
    ...renderDesignTokens({
      ...tokens,
      'disabled-fg': dt.tokens['color-action-text-control-disabled'],
      'disabled-bd': dt.tokens['color-action-border-control-disabled'],
      'disabled-bg': dt.tokens['color-action-background-control-disabled'],
      'focus-outline': outline,
      'focus-outline-offset': outlineOffset,
      'alert-bg': dt.tokens['color-action-background-control-alert'],
      'alert-bd': dt.tokens['color-action-border-control-alert'],
      'warning-bg': dt.tokens['color-action-background-control-warning'],
      'warning-bd': dt.tokens['color-action-border-control-warning'],
      'bd-width': `0 0 ${dt.tokens['size-border-width-s']} 0`,
      'bottom-bd-anim-dur': dt.tokens['duration-300'],
      'bottom-bd-anim-tm-ease-in': dt.tokens['ease-in'],
      'bottom-bd-anim-tm-ease-out': dt.tokens['ease-out']
    }, 'date-picker')
  };
};
//# sourceMappingURL=DatePicker.js.map